// 260528nrai: GenoEasy iframe — ready → license → badok:load; badok:save → BADOK

const LICENSE_ACK_MESSAGE_TYPES = [
  'genoeasy:licensed',
  'genoeasy:license-ok',
  'genoeasy:license:ok',
  'genoeasy:license:accepted',
];

const READY_MESSAGE_TYPE = 'genoeasy:ready';
const READY_FALLBACK_MS = 12000;
const DATA_DELAY_AFTER_LICENSE_MS = 500;
const CLEAR_SESSION_TIMEOUT_MS = 800;

let activeGenogramSession = null;

function normalizeOrigin(origin) {
  return String(origin || '').replace('127.0.0.1', 'localhost');
}

function originMatches(eventOrigin, expectedOrigin) {
  return normalizeOrigin(eventOrigin) === normalizeOrigin(expectedOrigin);
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function hasLoadData(data) {
  if (!data) { return false; }
  if (Array.isArray(data)) { return data.length > 0; }
  return typeof data === 'object';
}

function describeLoadData(data) {
  if (Array.isArray(data)) { return data.length + ' Beziehung(en)'; }
  if (data && typeof data === 'object') { return 'gespeichertes Genogramm'; }
  return 'keine Daten';
}

function logIframeMessage(event) {
  if (!event.data || !event.data.type) { return; }
  const messageType = String(event.data.type);
  if (!messageType.startsWith('genoeasy:') && !messageType.startsWith('badok:')) { return; }
  console.log('Genogramm [iframe→parent]', event.origin, messageType, event.data);
}

function handleGenoeasyMessage(event) {
  logIframeMessage(event);

  const session = activeGenogramSession;
  if (!session) { return; }
  if (!originMatches(event.origin, session.resolveTargetOrigin())) { return; }

  if (event.data.type === READY_MESSAGE_TYPE) {
    session.onReady();
    return;
  }

  if (LICENSE_ACK_MESSAGE_TYPES.includes(event.data.type)) {
    session.onLicenseAccepted(event.data.type);
    return;
  }

  if (event.data.type === 'badok:cleared') {
    session.onSessionCleared(event.data);
    return;
  }

  if (event.data.type === 'badok:error') {
    console.error('Genogramm [badok:error]', event.data.reason || event.data);
    return;
  }

  if (event.data.type === 'badok:save') {
    session.persistSave(event.data.data);
  }
}

if (!window.genoeasyGenogramListenerAttached) {
  window.addEventListener('message', handleGenoeasyMessage);
  window.genoeasyGenogramListenerAttached = true;
}

function originFromUrl(url) {
  try {
    return normalizeOrigin(new URL(url, window.location.href).origin);
  } catch (error) {
    return null;
  }
}

function createGenogramSession(config) {
  const frame = document.getElementById(config.frameId);
  if (!frame) { return null; }

  const configuredOrigin = normalizeOrigin(config.origin);

  return {
    frame: frame,
    configuredOrigin: configuredOrigin,
    jwt: config.jwt,
    payload: config.payload,
    saveUrl: config.saveUrl,
    clearSessionBeforeLoad: Boolean(config.clearSessionBeforeLoad),
    pendingSaveData: null,
    saveTimer: null,
    waitingForSessionClear: false,
    readyReceived: false,
    licenseSent: false,
    licenseAccepted: false,
    dataSent: false,
    timers: [],

    clearTimers() {
      this.timers.forEach((timerId) => window.clearTimeout(timerId));
      this.timers = [];
    },

    schedule(callback, delayMs) {
      const timerId = window.setTimeout(callback, delayMs);
      this.timers.push(timerId);
    },

    resolveTargetOrigin() {
      const iframeSrc = this.frame.getAttribute('src') || '';
      const fromSrc = originFromUrl(iframeSrc);
      if (fromSrc && this.configuredOrigin && fromSrc !== this.configuredOrigin) {
        console.warn(
          'Genogramm: GENOGRAM_ORIGIN (' + this.configuredOrigin + ') ≠ iframe (' + fromSrc + ') — postMessage targetOrigin = iframe'
        );
      }
      return fromSrc || this.configuredOrigin;
    },

    postToIframe(message) {
      if (!this.frame.contentWindow) { return false; }
      const iframeSrc = this.frame.getAttribute('src') || '';
      if (!iframeSrc || iframeSrc === 'about:blank') {
        console.warn('Genogramm: iframe src noch nicht gesetzt — postMessage übersprungen');
        return false;
      }
      const targetOrigin = this.resolveTargetOrigin();
      try {
        this.frame.contentWindow.postMessage(message, targetOrigin);
        console.log('Genogramm: postMessage', message.type, '→ targetOrigin', targetOrigin);
        return true;
      } catch (error) {
        console.warn('Genogramm postMessage fehlgeschlagen:', error.message);
        return false;
      }
    },

    sendLicense(reason) {
      if (!this.jwt || this.licenseAccepted) { return false; }
      const licenseMessage = { type: 'genoeasy:license', token: this.jwt };
      if (!this.postToIframe(licenseMessage)) { return false; }
      this.licenseSent = true;
      console.log('Genogramm: genoeasy:license gesendet (' + reason + ')');
      return true;
    },

    sendData(reason) {
      if (this.dataSent || !hasLoadData(this.payload.data)) { return; }
      if (!this.postToIframe(this.payload)) { return; }
      this.dataSent = true;
      console.log('Genogramm: badok:load gesendet (' + reason + ')');
    },

    beginLoadSequence(reason) {
      if (this.clearSessionBeforeLoad) {
        console.log('Genogramm: badok:clearSession vor load (IndexedDB-Cache leeren)');
        this.waitingForSessionClear = true;
        this.postToIframe({ type: 'badok:clearSession' });
        this.schedule(() => {
          if (this.waitingForSessionClear) {
            console.warn('Genogramm: badok:cleared Timeout — badok:load trotzdem');
            this.waitingForSessionClear = false;
            this.sendData(reason + ' (nach clearSession-Timeout)');
          }
        }, CLEAR_SESSION_TIMEOUT_MS);
        return;
      }
      this.sendData(reason);
    },

    scheduleLoadSequence(reason) {
      this.schedule(() => {
        this.beginLoadSequence(reason);
      }, DATA_DELAY_AFTER_LICENSE_MS);
    },

    onSessionCleared(data) {
      if (!this.waitingForSessionClear) { return; }
      this.waitingForSessionClear = false;
      console.log('Genogramm: badok:cleared', data);
      this.sendData('nach badok:clearSession');
    },

    persistSave(genogramData) {
      console.log('Genogramm [badok:save] empfangen');
      if (!this.saveUrl) {
        console.error('Genogramm: badok:save — keine saveUrl (nur Klienten-Beziehungen?)');
        return;
      }
      if (!genogramData || typeof genogramData !== 'object') {
        console.warn('Genogramm: badok:save ohne Daten — übersprungen');
        return;
      }
      this.pendingSaveData = genogramData;
      if (this.saveTimer) { window.clearTimeout(this.saveTimer); }
      this.saveTimer = window.setTimeout(() => {
        this.flushPersistSave();
      }, 300);
    },

    flushPersistSave() {
      const genogramData = this.pendingSaveData;
      this.pendingSaveData = null;
      if (!genogramData || !this.saveUrl) { return; }

      fetch(this.saveUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ data: genogramData }),
      }).then((response) => {
        if (!response.ok) {
          console.error('Genogramm: Speichern fehlgeschlagen (HTTP ' + response.status + ')');
          return;
        }
        console.log('Genogramm: in BADOK gespeichert');
      }).catch((error) => {
        console.error('Genogramm: Speichern fehlgeschlagen:', error.message);
      });
    },

    onReady() {
      if (this.readyReceived) { return; }
      this.readyReceived = true;
      this.clearTimers();
      console.log('Genogramm: genoeasy:ready empfangen — Leitfaden: license, dann badok:load');
      this.sendLicense('nach genoeasy:ready');
      this.scheduleLoadSequence('nach genoeasy:ready + Lizenz');
    },

    onLicenseAccepted(reason) {
      if (this.licenseAccepted) { return; }
      this.licenseAccepted = true;
      console.log('Genogramm: Lizenz bestätigt (' + reason + ')');
      if (!this.dataSent) {
        this.scheduleLoadSequence('nach ' + reason);
      }
    },

    runReadyFallback() {
      if (this.readyReceived) { return; }
      console.warn('Genogramm: kein genoeasy:ready nach ' + READY_FALLBACK_MS + 'ms — Fallback license + load');
      this.sendLicense('Fallback ohne ready');
      this.scheduleLoadSequence('Fallback ohne ready');
    },

    runHandshake() {
      this.clearTimers();
      const parentOrigin = window.location.origin;
      const targetOrigin = this.resolveTargetOrigin();
      console.log('Genogramm: parent origin=' + parentOrigin + ', postMessage targetOrigin=' + targetOrigin);
      console.log('Genogramm: warte auf genoeasy:ready …');

      this.schedule(() => {
        this.runReadyFallback();
      }, READY_FALLBACK_MS);
    },
  };
}

export function mountGenoeasyGenogram(config) {
  if (activeGenogramSession) {
    activeGenogramSession.clearTimers();
  }

  activeGenogramSession = createGenogramSession(config);
  if (!activeGenogramSession) { return; }

  console.log('Genogramm: ' + describeLoadData(config.payload.data) + ' für badok:load');
  console.log('Genogramm payload:', config.payload);
  console.log('Genogramm iframe origin:', config.origin);
  if (config.saveUrl) {
    console.log('Genogramm: Speichern unter', config.saveUrl);
  } else {
    console.warn('Genogramm: kein Speicher-Endpoint konfiguriert');
  }

  if (!config.jwt) {
    console.error('Genogramm: JWT fehlt — GENOEASY_API_KEY / Partner-API prüfen');
    return;
  }

  activeGenogramSession.frame.addEventListener('load', () => {
    console.log('Genogramm: iframe load, src=' + activeGenogramSession.frame.getAttribute('src'));
    activeGenogramSession.runHandshake();
  });
}

window.mount_genoeasy_genogram = mountGenoeasyGenogram;

export function unmountGenoeasyGenogram() {
  if (activeGenogramSession) {
    activeGenogramSession.clearTimers();
    if (activeGenogramSession.saveTimer) {
      window.clearTimeout(activeGenogramSession.saveTimer);
    }
  }
  activeGenogramSession = null;
}

window.unmount_genoeasy_genogram = unmountGenoeasyGenogram;
